import { supabase } from '../lib/supabase'
import { formatDateToYMD, getWeekStartMonday, startOfDay } from './metricDateUtils'

const safeNumber = (value) => {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : 0
}

const clamp0 = (value) => (value < 0 ? 0 : value)

const normalizeMetrics = (data) => {
  return {
    revenue: safeNumber(data?.revenue),
    expenses: safeNumber(data?.expenses),
    leads: safeNumber(data?.leads),
    customers: safeNumber(data?.customers),
    // social_following: safeNumber(data?.social_following),
  }
}

const deltaMetrics = (previous, next) => {
  return {
    revenue: next.revenue - previous.revenue,
    expenses: next.expenses - previous.expenses,
    leads: next.leads - previous.leads,
    customers: next.customers - previous.customers,
    // social_following: next.social_following - previous.social_following,
  }
}

const getMonthYear = (date) => {
  const d = startOfDay(date)
  return { month: d.getMonth() + 1, year: d.getFullYear() }
}

const upsertMonthlyWithDelta = async ({ businessId, date, delta }) => {
  const { month, year } = getMonthYear(date)

  const { data: existing, error: fetchError } = await supabase
    .from('monthly_metrics')
    .select('revenue, expenses, leads, customers')

  // NOTE: social_following rollups are temporarily disabled.
    .eq('business_id', businessId)
    .eq('month', month)
    .eq('year', year)
    .maybeSingle()

  if (fetchError) throw fetchError

  const base = normalizeMetrics(existing)

  const payload = {
    business_id: businessId,
    month,
    year,
    revenue: clamp0(base.revenue + delta.revenue),
    expenses: clamp0(base.expenses + delta.expenses),
    leads: clamp0(base.leads + delta.leads),
    customers: clamp0(base.customers + delta.customers),

    // social_following is intentionally not updated for now.
    // social_following: Math.max(base.social_following, safeNumber(nextSocialFollowing)),
  }

  const { error: upsertError } = await supabase
    .from('monthly_metrics')
    .upsert(payload, { onConflict: 'business_id,month,year' })

  if (upsertError) throw upsertError
}

const upsertWeeklyWithDelta = async ({ businessId, date, delta }) => {
  const weekStart = getWeekStartMonday(date)
  const weekKey = formatDateToYMD(weekStart)

  const { data: existing, error: fetchError } = await supabase
    .from('weekly_metrics')
    .select('revenue, expenses, leads, customers')

  // NOTE: social_following rollups are temporarily disabled.
    .eq('business_id', businessId)
    .eq('week_start_date', weekKey)
    .maybeSingle()

  if (fetchError) throw fetchError

  const base = normalizeMetrics(existing)

  const payload = {
    business_id: businessId,
    week_start_date: weekKey,
    revenue: clamp0(base.revenue + delta.revenue),
    expenses: clamp0(base.expenses + delta.expenses),
    leads: clamp0(base.leads + delta.leads),
    customers: clamp0(base.customers + delta.customers),

    // social_following is intentionally not updated for now.
    // social_following: Math.max(base.social_following, safeNumber(nextSocialFollowing)),
  }

  const { error: upsertError } = await supabase
    .from('weekly_metrics')
    .upsert(payload, { onConflict: 'business_id,week_start_date' })

  if (upsertError) throw upsertError
}

export const applyDailyMetricsAggregatesUpdate = async ({
  businessId,
  date,
  previous,
  next,
}) => {
  if (!businessId || !date) return

  const previousNumeric = normalizeMetrics(previous)
  const nextNumeric = normalizeMetrics(next)
  const delta = deltaMetrics(previousNumeric, nextNumeric)

  // A daily save should add/subtract from the week and the month (not overwrite them).
  await upsertWeeklyWithDelta({
    businessId,
    date,
    delta,
    // nextSocialFollowing: nextNumeric.social_following,
  })

  await upsertMonthlyWithDelta({
    businessId,
    date,
    delta,
    // nextSocialFollowing: nextNumeric.social_following,
  })
}

export const applyWeeklyMetricsAggregatesUpdate = async ({
  businessId,
  weekStartDate,
  previous,
  next,
}) => {
  if (!businessId || !weekStartDate) return

  const previousNumeric = normalizeMetrics(previous)
  const nextNumeric = normalizeMetrics(next)
  const delta = deltaMetrics(previousNumeric, nextNumeric)

  // Convention: weekly entries belong to the month containing the week_start_date.
  await upsertMonthlyWithDelta({
    businessId,
    date: weekStartDate,
    delta,
    // nextSocialFollowing: nextNumeric.social_following,
  })
}
